import { type DataTableKpiDisplayNameType, type DataTableViewType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, type Ref, ref, unref, watch } from 'vue';

import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { getDataTableAvailableKpisList } from '#domain/performance/kpi/kpi.query';
import { useDataTableStore } from '#stores/dataTable.store';

interface UseDataTableKpisParams {
  dataTableView: Ref<DataTableViewType>;
}

export function useDataTableKpis({ dataTableView }: UseDataTableKpisParams) {
  const dataTableStore = useDataTableStore();
  const {
    taxonomy: { taxonomy },
    period: { selectedPeriod }
  } = useKpisPerimeter();

  const fetchDataTableKpis = async () => {
    return getDataTableAvailableKpisList({
      dataTableView: unref(dataTableView),
      taxonomy: unref(taxonomy),
      period: unref(selectedPeriod).period
    });
  };

  const { data: availableKpis, isFetching: isFetchingAvailableKpis } = useQuery({
    queryKey: ['availableKpis', dataTableView, taxonomy, selectedPeriod],
    queryFn: fetchDataTableKpis
  });

  function getSavedSelectedKpisFiltered(kpis: typeof availableKpis.value) {
    if (!kpis) {
      return null;
    }

    const savedSelectedKpis = dataTableStore.getSelectedKpis(taxonomy.value, dataTableView.value);

    return savedSelectedKpis?.filter((savedSelectedKpi) => kpis?.find((kpi) => savedSelectedKpi === kpi.name));
  }

  const selectedKpis = ref<DataTableKpiDisplayNameType[]>(
    getSavedSelectedKpisFiltered(availableKpis.value) ?? getDefaultSelectedKpis()
  );
  const previousAvailableKpis = ref<typeof availableKpis.value | null>(null);

  watch(dataTableView, () => {
    selectedKpis.value = [];
    previousAvailableKpis.value = null;
  });

  watch(availableKpis, (availableKpis) => {
    if (availableKpis?.length) {
      const newlyAvailableKpis = previousAvailableKpis.value
        ? availableKpis.filter(
            (kpi) => !previousAvailableKpis.value?.some((previousKpi) => previousKpi.name === kpi.name)
          )
        : [];

      updateSelectedKpis([
        ...(getSavedSelectedKpisFiltered(availableKpis) ?? getDefaultSelectedKpis()),
        ...newlyAvailableKpis.map((kpi) => kpi.name)
      ]);

      previousAvailableKpis.value = availableKpis;
    }
  });

  function getDefaultSelectedKpis() {
    return availableKpis.value?.length ? availableKpis.value.map((kpi) => kpi.name) : [];
  }

  function updateSelectedKpis(kpis: DataTableKpiDisplayNameType[]) {
    selectedKpis.value = kpis;
    dataTableStore.updateSelectedKpis(taxonomy.value, dataTableView.value, kpis);
  }

  function resetSelectedKpis() {
    dataTableStore.resetSelectedKpis(taxonomy.value, dataTableView.value);
    updateSelectedKpis(getDefaultSelectedKpis());
  }

  const areAvailableKpisFetched = computed(() => (selectedKpis.value?.length ?? 0) > 0);

  return {
    availableKpis,
    selectedKpis,
    updateSelectedKpis,
    resetSelectedKpis,
    areAvailableKpisFetched,
    isFetchingAvailableKpis
  };
}
